import { Navigate, useLocation } from 'react-router-dom';
import { usePermission } from '../../hooks/usePermission';
import { hasUsableSession } from '../../utils/authHelpers';

/**
 * 권한 기반 라우트 보호 컴포넌트
 *
 * 1단계: 로그인 여부 체크 → 미로그인이면 / (어드민 진입점)으로
 * 2단계: 지정된 권한 보유 여부 체크 → 없으면 본인 defaultRoute로
 *
 * @param {React.ReactNode} children - 렌더링할 자식 컴포넌트
 * @param {string} requiredPermission - 필요한 권한 (canViewDashboard, canViewKolsDB, canViewMyKols, canViewCampaign, canViewShipping, canViewDelivery, canViewInvoice)
 */
function ProtectedRoute({ children, requiredPermission }) {
  const location = useLocation();
  const permissions = usePermission();
  const { defaultRoute } = permissions;
  /* 토큰만 보면 안 된다 — 반쪽 상태(토큰은 있고 serviceType 이 없음)면
     여기서 `/` 로 보내고 GuestOnlyRoute 가 다시 여기로 보내며 무한히 돈다(2026-08-01). */
  // 못 쓰는 세션이면 어드민 진입점(/)으로 보내고, 원래 경로 기억
  if (!hasUsableSession()) {
    return <Navigate to="/" replace state={{ from: location.pathname + location.search }} />;
  }

  // 권한이 지정되지 않은 경우 그냥 렌더링
  if (!requiredPermission) {
    return children;
  }

  // 해당 권한이 있는지 확인
  const hasPermission = permissions[requiredPermission];

  // 권한이 없으면 본인 기본 라우트로 리다이렉트.
  // 단, 기본 라우트 자체에 권한이 없으면(serviceType 이 비었거나 권한 표에서 다 꺼진 경우)
  // 같은 곳으로 계속 튕겨 무한 리다이렉트가 된다 → 그럴 땐 로그인 진입점으로 보낸다.
  if (!hasPermission) {
    const target = location.pathname === defaultRoute ? '/' : defaultRoute;
    return <Navigate to={target} replace />;
  }

  return children;
}

export default ProtectedRoute;
